import { useSelector } from "react-redux";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { TableTop } from "app/components/React/TopSites/DomainsTable/TableTop";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    DesktopMobileShare,
    getBonusCountryColumns,
    getColumns as defaultGetColumns,
} from "app/components/React/TopSites/DomainsTable/getColumns";
import React, { useState } from "react";
import { getInitialFilters } from "app/components/React/TopSites/DomainsTable/getInitialFilters";
import { transformDataHof } from "app/components/React/TopSites/DomainsTable/transformData";
import { TopMoversStrip } from "app/components/React/TopSites/DomainsTable/website-rankings-momentum/components/TopMoversStrip";
import { useWebsiteRankingsMomentum } from "app/components/React/TopSites/DomainsTable/website-rankings-momentum/useWebsiteRankingsMomentum";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { swSettings } from "app/common/services/swSettings";
import { useCurrentStateAndParams } from "@uirouter/react";
import { getSupportPeriod } from "app/UtilitiesAndConstants/UtilityFunctions/periodSupport";
import { createCategoryService, ALL } from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import { Stack } from "@similarweb/ui-components/foundations";

const TABLE_ENDPOINT = "/api/Market/Leaders/Table";
const EXCEL_ENDPOINT = "api/Market/Leaders/Excel";
const BONUS_COUNTRY_TABLE_ENDPOINT = "/widgetApi/TopSitesNew/TopSites/Table";
const BONUS_COUNTRY_EXCEL_ENDPOINT = "widgetApi/TopSitesNew/TopSites/Excel";

const getIsBonusCountry = (country: string): boolean => {
    const availableCountries = swSettings.components.Home.resources.Countries;
    const isBonusCountry = !availableCountries.includes(Number(country));
    return isBonusCountry;
};

interface ISortState {
    field: string;
    sortDirection: string;
}

export const IndustryLeadersTable = () => {
    const { params } = useCurrentStateAndParams();
    const categoryService = createCategoryService();
    const { webSource, country, category } = params;
    const [sort, setSort] = useState<ISortState>({
        field: "Share",
        sortDirection: "desc",
    });
    const [isBonusCountry, setIsBonusCountry] = useState<boolean>(getIsBonusCountry(country));
    const {
        topMovers,
        isLoading: isTopMoversLoading,
        isError: isTopMoversError,
        onDataFetch,
        getDataCallback,
        onDataError,
    } = useWebsiteRankingsMomentum();
    const onSort = ({ field, sortDirection }: ISortState) => {
        setSort({ field, sortDirection });
    };
    const segmentsModule: ISegmentsModule = useSelector((state) => {
        const { segmentsModule } = state;
        return segmentsModule;
    });

    const monthlySupport = swSettings.components.IndustryAnalysis.resources.Duration as number;
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;
    const periodSupport = getSupportPeriod(monthlySupport);
    const industry: ICategory = categoryService.categoryQueryParamToCategoryObject(category);

    const isAllIndustries = !industry || industry.id === ALL;
    const isCustomIndustry = industry?.isCustomCategory;
    const isTopLevelCategory = industry?.children?.length > 0;
    const hasSegments = CategoryServiceUtils.hasSegments(industry);
    const showIndustryColumn = isAllIndustries || isCustomIndustry || isTopLevelCategory;

    const columns = React.useMemo(() => {
        const excludeFields = [webSource !== devicesTypes.TOTAL && DesktopMobileShare];
        return isBonusCountry
            ? getBonusCountryColumns(excludeFields, sort, hasSegments, showIndustryColumn)
            : defaultGetColumns(excludeFields, sort, periodSupport, hasSegments, showIndustryColumn, webSource);
    }, [sort, webSource, isBonusCountry, showIndustryColumn, hasSegments, periodSupport]);

    React.useEffect(() => {
        setIsBonusCountry(getIsBonusCountry(country));
    }, [country]);
    const initialFilters = getInitialFilters(params, sort);
    const i18n = i18nFilter();

    return (
        <Stack gap="24px">
            {!isBonusCountry && (
                <TopMoversStrip
                    movers={topMovers}
                    isLoading={isTopMoversLoading}
                    isError={isTopMoversError}
                />
            )}
            <SWReactTableWrapper
                initialFilters={initialFilters}
                transformData={transformDataHof(segmentsModule)}
                getDataCallback={getDataCallback}
                onDataFetch={onDataFetch}
                onDataError={onDataError}
                onSort={onSort}
                serverApi={isBonusCountry ? BONUS_COUNTRY_TABLE_ENDPOINT : TABLE_ENDPOINT}
                tableColumns={columns}
                tableOptions={{
                    noDataTitle: i18n("industry.leaders.table.no.data.title"),
                    noDataSubtitle: i18n("industry.leaders.table.no.data.subtitle"),
                    tableSelectionTrackingParam: "keyword",
                }}
                recordsField="records"
                totalRecordsField="TotalCount"
                pageIndent={1}
            >
                {(topComponentProps) => (
                    <TableTop
                        {...topComponentProps}
                        initialFilters={initialFilters}
                        params={params}
                        excelEndpoint={isBonusCountry ? BONUS_COUNTRY_EXCEL_ENDPOINT : EXCEL_ENDPOINT}
                        sort={sort}
                        downloadExcelPermitted={downloadExcelPermitted}
                    />
                )}
            </SWReactTableWrapper>
        </Stack>
    );
};
